/**
 * D16-B — Supervisor authenticated signaling lifecycle + handshake controller.
 *
 * The Supervisor is a DISTINCT authenticated participant from the Operator, so it owns its OWN socket —
 * but it derives every envelope + schema + correlation rule from the single canonical protocol authority
 * (webrtcSignalingProtocol). It never touches the Operator socket or the primary Customer↔Operator PC, and
 * never constructs a Customer signaling target. The transport is `createSocket`-injectable so the lifecycle
 * + handshake are unit-testable without a live browser (physical audio proof stays Milestone-6).
 */
import * as P from './webrtcSignalingProtocol';
import {
  wireSupervisorLegTransport, createSupervisorOffer, applySupervisorAnswer, addSupervisorIce,
} from './supervisionMedia';

/** Owns ONE authenticated Supervisor socket: endpoint + auth-frame-first + generation (supersede) guard. */
export function createSupervisorTransport({ wsUrl, businessId, managerId, getToken, plane = 'business_web', installationId, createSocket } = {}) {
  const openSocket = createSocket || ((url) => new WebSocket(url));
  const endpoint = P.buildSignalingEndpoint({ wsUrl, businessId, userId: managerId });
  let socket = null; let authed = false; let generation = 0;
  // P0-D connect fix: frames sent before the socket finished opening were DROPPED (readyState !== 1).
  // The supervisor-offer is created + sent within a few ms of open(), so it raced — and lost against —
  // the WS handshake and never reached the operator (only later-gathered ICE got through: the observed
  // "ice-only, no offer/answer" fingerprint). Queue pre-open frames and flush them, IN ORDER, on open.
  const outbox = [];
  const flush = () => {
    if (!socket || socket.readyState !== 1) return;
    while (outbox.length) { try { socket.send(JSON.stringify(outbox.shift())); } catch (_) { break; } }
  };

  function open(onMessage) {
    const gen = ++generation;               // supersede any prior socket generation (reconnect-safe)
    socket = openSocket(endpoint);
    socket.onopen = async () => {
      if (gen !== generation) return;        // superseded while opening
      const token = (getToken ? await getToken() : '') || '';
      // AUTH FRAME FIRST — canonical builder (identical envelope to RT-3). Never log token.
      try { socket.send(JSON.stringify(P.buildAuthFrame({ token, plane, nonce: P.makeNonce(), installationId }))); } catch (_) { /* legacy backend tolerant */ }
      flush();                               // then deliver anything queued before open (offer/diag) in order
    };
    socket.onmessage = (ev) => {
      if (gen !== generation) return;        // drop stale-generation frames
      const msg = P.safeParse(ev.data);
      if (!msg) return;
      if (P.isAuthAck(msg)) { authed = true; flush(); return; }
      if (typeof onMessage === 'function') onMessage(msg);
    };
    return socket;
  }
  const send = (obj) => {
    if (socket && socket.readyState === 1) { try { socket.send(JSON.stringify(obj)); return; } catch (_) { /* fall through to queue */ } }
    outbox.push(obj);                        // not open yet → queue; flush() delivers on open/auth
  };
  const close = () => { generation++; try { if (socket) socket.close(); } catch (_) { /* noop */ } socket = null; authed = false; outbox.length = 0; };
  return { open, send, close, endpoint, isAuthed: () => authed, currentGeneration: () => generation };
}

/**
 * Pure handshake controller: drives ONE auxiliary Supervisor↔Operator leg over the injected transport +
 * backend confirm. Rejects any frame that does not correlate to the active call + supervision session.
 */
export function createSupervisorHandshake({ transport, leg, callId, supervisionSessionId, operatorTarget, confirm, onListening, onError, diag, diagMeta, frameMeta } = {}) {
  const _fm = frameMeta || {};   // SUPERVISION_CLIENT_CONTRACT_V1 id set echoed on offer + ICE (operator requires it)
  // ── P0-D observability (diagnostic-only) ──────────────────────────────────────────────────────
  // A `diag(event, extra)` sink, when provided, emits SAFE supervision stage beacons (names/counts/
  // states only — never SDP/ICE bodies). It NEVER changes media behavior: listeners are attached with
  // addEventListener (they do not clobber the onicecandidate/onconnectionstatechange handlers set by
  // wireSupervisorLegTransport), the `track` listener ONLY counts (no attach, no play), and every emit
  // is wrapped so a diagnostics failure can never break the handshake.
  let candidatesSent = 0, candidatesReceived = 0, remoteTrackEvents = 0;
  const emit = (event, extra = {}) => { if (typeof diag === 'function') { try { diag(event, extra); } catch (_) { /* diagnostics never break media */ } } };
  const pc = leg && leg.pc;
  if (pc && typeof pc.addEventListener === 'function' && typeof diag === 'function') {
    try {
      pc.addEventListener('icecandidate', (e) => { if (e && e.candidate) candidatesSent += 1; else emit('MANAGER_ICE_GATHERING_DONE', { candidates_sent: candidatesSent }); });
      pc.addEventListener('icegatheringstatechange', () => emit('MANAGER_ICE_GATHERING_STATE', { ice_gathering_state: pc.iceGatheringState, candidates_sent: candidatesSent }));
      pc.addEventListener('iceconnectionstatechange', () => emit('MANAGER_ICE_CONNECTION_STATE', { ice_connection_state: pc.iceConnectionState, candidates_sent: candidatesSent, candidates_received: candidatesReceived }));
      pc.addEventListener('connectionstatechange', () => emit('MANAGER_CONNECTION_STATE', { connection_state: pc.connectionState }));
      // COUNT ONLY — diagnostic; does not attach the stream or call play() (playback fix is a later train).
      pc.addEventListener('track', () => { remoteTrackEvents += 1; emit('MANAGER_REMOTE_TRACK_EVENT', { remote_track_event_count: remoteTrackEvents }); });
    } catch (_) { /* addEventListener unsupported (test stub) — diagnostics are best-effort */ }
  }
  wireSupervisorLegTransport(leg, {
    // Normalise ICE onto the canonical supervision schema; only ever addressed to the operator target.
    sendSignal: (m) => transport.send(P.buildSupervisorIce(m.candidate, operatorTarget, callId, supervisionSessionId, _fm)),
    onConnected: async () => {
      try { if (confirm) await confirm(); if (onListening) onListening(); }
      catch (e) { if (onError) onError(e); }
    },
  });
  const counts = () => {
    try { return { transceiver_count: (pc.getTransceivers && pc.getTransceivers().length) || 0,
                   sender_count: (pc.getSenders && pc.getSenders().length) || 0,
                   receiver_count: (pc.getReceivers && pc.getReceivers().length) || 0 }; }
    catch (_) { return {}; }
  };
  return {
    async start() {
      emit('MANAGER_SUPERVISION_PC_CREATED', { mode: (diagMeta && diagMeta.mode), ice_server_count: (diagMeta && diagMeta.ice_server_count), turn_server_count: (diagMeta && diagMeta.turn_server_count), ...counts() });
      const offer = await createSupervisorOffer(leg);
      emit('MANAGER_OFFER_CREATED', { signaling_state: pc && pc.signalingState, ...counts() });
      transport.send(P.buildSupervisorOffer(offer, operatorTarget, callId, supervisionSessionId, _fm));
      emit('MANAGER_OFFER_SENT', { signaling_state: pc && pc.signalingState });
    },
    async onSignal(msg) {
      if (!P.correlatesTo(msg, { callId, supervisionSessionId })) return; // stale / wrong call / wrong session
      if (msg.type === P.SIGNAL.SUPERVISOR_ANSWER) {
        emit('MANAGER_ANSWER_RECEIVED', {});
        await applySupervisorAnswer(leg, msg.answer);
        emit('MANAGER_REMOTE_DESCRIPTION_APPLIED', { signaling_state: pc && pc.signalingState });
      } else if (msg.type === P.SIGNAL.SUPERVISOR_ICE) {
        candidatesReceived += 1;
        await addSupervisorIce(leg, msg.candidate);
        emit('MANAGER_ICE_CANDIDATE_RECEIVED', { candidates_received: candidatesReceived });
      }
    },
  };
}
